import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getFormsData, deleteForm, duplicateForm } from './formHelpers/formHelper';
import DataGridComponent from './formComponents/DataGridComponent';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import './styles/formListScreen.scss';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import crudProfiler from '../../pages/utils/CRUDPerformanceProfiler';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

/**
 * Componente que muestra una lista de formularios en un DataGrid con funcionalidad de búsqueda y acciones.
 *
 * @component
 * @returns {JSX.Element} - React JSX que representa el componente FormListScreen.
 */
const FormListScreen = () => {
  const { currentUser } = useContext(AuthContext);
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [servicioId, setServicioId] = useState(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateFormId, setDuplicateFormId] = useState(null);
  const [duplicateFormName, setDuplicateFormName] = useState('');
  const [duplicateNewName, setDuplicateNewName] = useState('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  /**
 * Función para obtener y establecer la información de los formularios al cargar el componente.
 *
 * @async
 * @function
 * @returns {Promise<void>} - Una promesa que se resuelve después de obtener y establecer la información de los formularios.
 */
  useEffect(() => {
    const fetchData = async () => {
      // 📊 Iniciar sesión de profiling
      crudProfiler.startSession('LoadFormsList', {
        entity: 'FormsList',
        userId: currentUser?.uid
      });

      // 📊 Medir carga de formularios
      crudProfiler.startMeasurement('68_FetchFormsList', {
        userId: currentUser?.uid
      });

      try {
        const formsData = await getFormsData(currentUser, setServicioId, setLoading);

        crudProfiler.endMeasurement('68_FetchFormsList', {
          success: true,
          formsCount: formsData.length
        });

        // 📊 Medir procesamiento de datos
        crudProfiler.startMeasurement('69_ProcessForms', {
          formsToProcess: formsData.length
        });

        crudProfiler.endMeasurement('69_ProcessForms', {
          formsProcessed: formsData.length
        });

        // 📊 Medir actualización de estado
        crudProfiler.startMeasurement('70_UpdateState');

        setFormularios(formsData);

        crudProfiler.endMeasurement('70_UpdateState', {
          formsSet: formsData.length
        });

        // 📊 Finalizar sesión exitosa
        crudProfiler.endSession({
          success: true,
          formsLoaded: formsData.length
        });
      } catch (error) {
        console.error('Error al obtener los formularios:', error);

        crudProfiler.endMeasurement('68_FetchFormsList', {
          success: false,
          error: error.message
        });

        crudProfiler.endSession({
          success: false,
          error: error.message
        });
      }
    };

    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  /**
 * Función para manejar la eliminación de un formulario.
 *
 * @async
 * @function
 * @param {string} id - El ID del formulario a eliminar.
 * @returns {Promise<void>} - Una promesa que se resuelve después de eliminar el formulario.
 */
  const handleDuplicateClick = (row) => {
    setDuplicateFormId(row.id);
    setDuplicateFormName(row.name || '');
    setDuplicateNewName((row.name || '') + ' (copia)');
    setDuplicateError('');
    setDuplicateOpen(true);
  };

  const handleDuplicateClose = () => {
    setDuplicateOpen(false);
    setDuplicateFormId(null);
    setDuplicateFormName('');
    setDuplicateNewName('');
    setDuplicateError('');
  };

  const handleDuplicateConfirm = async () => {
    const name = (duplicateNewName || '').trim();
    if (!name) {
      setDuplicateError('Escribe un nombre para la copia');
      return;
    }
    setDuplicateError('');
    setDuplicateLoading(true);
    try {
      await duplicateForm(duplicateFormId, servicioId, name, setFormularios);
      handleDuplicateClose();
    } catch (err) {
      setDuplicateError(err.message || 'Error al duplicar el formulario');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const originalForm = formularios.find((form) => form.id === id);
    if (originalForm) {
      // 📊 Iniciar sesión de profiling para eliminación
      crudProfiler.startSession('DeleteForm', {
        entity: 'Form',
        formId: id,
        formName: originalForm.name
      });

      // 📊 Medir eliminación
      crudProfiler.startMeasurement('71_DeleteFormRecord', {
        formId: id
      });

      try {
        await deleteForm(originalForm.id, servicioId, setFormularios);

        crudProfiler.endMeasurement('71_DeleteFormRecord', {
          success: true,
          formDeleted: id
        });

        crudProfiler.endSession({
          success: true,
          formDeleted: id
        });
      } catch (error) {
        crudProfiler.endMeasurement('71_DeleteFormRecord', {
          success: false,
          error: error.message
        });

        crudProfiler.endSession({
          success: false,
          error: error.message
        });
      }
    }
  };


  const columns = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    { field: 'name', headerName: 'Nombre del Formulario', width: 200 },
    {
      field: 'export',
      headerName: 'Tipo de exportación',
      width: 150,
      renderCell: (params) => (
        <div style={{
          borderRadius: '10px',
          background: 'rgba(73, 80, 87, 0.27)',
          padding: '5px 10px',
          color: 'black',
          whiteSpace: 'nowrap'  // Esto evita que los elementos se rompan en varias líneas
        }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'cliente',
      headerName: 'Clientes',
      width: 200,
      renderCell: (params) => (
        <span>{Array.isArray(params.value) ? params.value.join(', ') : ''}</span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      renderCell: (params) => (
        <div className="cellAction">
          <Link to={`/form-update/${encodeURIComponent(params.row.id)}`} style={{ textDecoration: "none" }}>
            <div className="updateButton">Editar</div>
          </Link>
          <ContentCopyIcon
            className="duplicateIcon"
            titleAccess="Duplicar"
            onClick={() => handleDuplicateClick(params.row)}
          />
          <DeleteIcon className="deleteIcon" onClick={() => handleDelete(params.row.id)} />
        </div>
      ),
    },
  ];

  /**
   * Formularios filtrados según el término de búsqueda.
   *
   * @type {Object[]}
   */
  const filteredFormularios = formularios.filter((formulario) =>
    (formulario.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h2 className="title-registro">Formularios</h2>
        <div className='button-container'>
          <div className="searchContainer">
            <input
              className="searchInput"
              label="Buscar"
              placeholder='Buscar'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchIcon className="searchIcon" />
          </div>
          <div className='add-container'>
            <Link to="/form-creator" className="link">
              Crear formulario
            </Link>
          </div>
        </div>
      </div>
      <div className="data-grid-container">
        <DataGridComponent
          rows={filteredFormularios}
          columns={columns}
        />
      </div>

      <Dialog open={duplicateOpen} onClose={handleDuplicateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicar formulario</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            Copia de &quot;{duplicateFormName}&quot;. Asigna un nombre a la copia:
          </p>
          <TextField
            autoFocus
            fullWidth
            label="Nombre del nuevo formulario"
            value={duplicateNewName}
            onChange={(e) => setDuplicateNewName(e.target.value)}
            error={!!duplicateError}
            helperText={duplicateError}
            disabled={duplicateLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateClose} disabled={duplicateLoading}>
            Cancelar
          </Button>
          <Button onClick={handleDuplicateConfirm} variant="contained" disabled={duplicateLoading}>
            {duplicateLoading ? 'Guardando...' : 'Duplicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FormListScreen;